import { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronDown, GitMerge, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  previewSkillMerge,
  useAddSkillAlias,
  useCreateSkillAdmin,
  useDeleteSkillAdmin,
  useMergeSkills,
  useRemoveSkillAlias,
  useRenameSkillAdmin,
  useSkillAdminDetail,
  useSkillsAdminList,
} from '@/hooks/use-recruitment';
import type {
  SkillAdminListItem,
  SkillAliasConflict,
  SkillMergePreview,
} from '@/types/recruitment';

interface GlobalCandidateSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalCandidateSettingsDialog({
  open,
  onOpenChange,
}: GlobalCandidateSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] w-[720px] max-w-[720px] flex-col gap-0 overflow-hidden rounded-xl p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Candidate Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage canonical skills and their aliases.
        </DialogDescription>

        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-[18px]">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-[18px] w-[18px] shrink-0 text-muted-foreground" aria-hidden />
            <span className="text-base font-bold tracking-tight">Candidate Settings</span>
          </div>
          <button
            type="button"
            className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <Tabs defaultValue="skill-aliases" className="flex min-h-0 flex-1 flex-col">
          <TabsList
            className="h-auto w-full shrink-0 justify-start gap-0 rounded-none border-b border-border bg-muted/40 p-0 px-6 [&>button]:bg-transparent [&>button[data-state=active]]:bg-transparent [&>button[data-state=active]]:shadow-none"
          >
            <TabsTrigger
              value="skill-aliases"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:font-semibold data-[state=active]:text-foreground"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Skill Aliases
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="skill-aliases" className="mt-0 p-5">
              <SkillAliasesPanel />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ── Skill Aliases Panel ────────────────────────────────────────

function SkillAliasesPanel() {
  const [search, setSearch] = useState('');
  const { data: skills, isLoading } = useSkillsAdminList(search);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const createMutation = useCreateSkillAdmin();
  const [newSkillName, setNewSkillName] = useState('');

  const handleCreate = () => {
    const name = newSkillName.trim();
    if (!name) return;
    createMutation.mutate(name, {
      onSuccess: (created) => {
        setNewSkillName('');
        setCreateOpen(false);
        setExpandedId(created.id);
      },
      onError: (error) => {
        toast.error(extractConflictMessage(error) ?? 'Could not create skill');
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Skill Aliases</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Canonical skills used across candidate profiles and vacancies. Add alternate
            spellings so different variants collapse into one chip.
          </p>
        </div>
        {createOpen ? (
          <div className="flex shrink-0 items-center gap-2">
            <Input
              autoFocus
              value={newSkillName}
              maxLength={100}
              placeholder="New skill name"
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') {
                  setCreateOpen(false);
                  setNewSkillName('');
                }
              }}
              className="h-9 w-48"
            />
            <Button
              size="sm"
              className="h-9 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
              onClick={handleCreate}
              disabled={!newSkillName.trim() || createMutation.isPending}
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-9 cursor-pointer"
              onClick={() => {
                setCreateOpen(false);
                setNewSkillName('');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            className="h-9 shrink-0 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New Skill
          </Button>
        )}
      </div>

      <Input
        value={search}
        placeholder="Search skills or aliases..."
        onChange={(e) => setSearch(e.target.value)}
        className="h-9"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          Loading skills...
        </div>
      ) : !skills || skills.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border py-10 text-center">
          <Sparkles className="h-7 w-7 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {search ? 'No matching skills' : 'No skills yet'}
          </p>
          <p className="text-xs text-muted-foreground">
            Add a skill above so candidate/vacancy chips collapse on consistent forms.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {skills.map((s) => (
            <SkillRow
              key={s.id}
              skill={s}
              expanded={expandedId === s.id}
              onToggle={() => setExpandedId((cur) => (cur === s.id ? null : s.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Skill Row ───────────────────────────────────────────────────

interface SkillRowProps {
  skill: SkillAdminListItem;
  expanded: boolean;
  onToggle: () => void;
}

function SkillRow({ skill, expanded, onToggle }: SkillRowProps) {
  return (
    <div className="rounded-md border border-border bg-background/40">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-foreground" title={skill.name}>
            {skill.name}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {skill.aliasCount} alias{skill.aliasCount === 1 ? '' : 'es'} ·{' '}
            {skill.candidateCount} candidate{skill.candidateCount === 1 ? '' : 's'} ·{' '}
            {skill.vacancyCount} vacanc{skill.vacancyCount === 1 ? 'y' : 'ies'}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border px-3 py-3">
          <SkillEditor skill={skill} />
        </div>
      )}
    </div>
  );
}

// ── Skill Editor ───────────────────────────────────────────────

function SkillEditor({ skill }: { skill: SkillAdminListItem }) {
  const { data: detail, isLoading } = useSkillAdminDetail(skill.id);
  const renameMutation = useRenameSkillAdmin();
  const addAliasMutation = useAddSkillAlias(skill.id);
  const removeAliasMutation = useRemoveSkillAlias(skill.id);
  const deleteMutation = useDeleteSkillAdmin();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(skill.name);
  const [aliasDraft, setAliasDraft] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<SkillAdminListItem | null>(null);

  const commitName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === skill.name) {
      setNameDraft(skill.name);
      setEditingName(false);
      return;
    }
    renameMutation.mutate(
      { id: skill.id, name: trimmed },
      {
        onSuccess: () => setEditingName(false),
        onError: (error) => {
          toast.error(extractConflictMessage(error) ?? 'Could not rename skill');
          setNameDraft(skill.name);
        },
      },
    );
  };

  const addAlias = () => {
    const trimmed = aliasDraft.trim();
    if (!trimmed) return;
    addAliasMutation.mutate(trimmed, {
      onSuccess: () => setAliasDraft(''),
      onError: (error) => {
        toast.error(extractConflictMessage(error) ?? 'Could not add alias');
      },
    });
  };

  const removeAlias = (aliasId: string) => {
    removeAliasMutation.mutate(aliasId, {
      onError: (error) => {
        toast.error(extractConflictMessage(error) ?? 'Could not remove alias');
      },
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(skill.id, {
      onSuccess: () => setDeleteOpen(false),
      onError: (error) => {
        toast.error(extractConflictMessage(error) ?? 'Could not delete skill');
      },
    });
  };

  if (isLoading || !detail) {
    return <div className="py-2 text-xs text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {editingName ? (
          <Input
            autoFocus
            value={nameDraft}
            maxLength={100}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setNameDraft(skill.name);
                setEditingName(false);
              }
            }}
            className="h-8 max-w-xs"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingName(true)}
            className="group inline-flex max-w-md cursor-pointer items-center gap-1.5 text-[13px] font-medium text-foreground hover:text-foreground"
            title={skill.name}
          >
            <span className="truncate">{skill.name}</span>
            <Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        )}
        <div className="flex-1" />
        <MergePicker
          currentSkillId={skill.id}
          onPick={(target) => setMergeTarget(target)}
        />
        <button
          type="button"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground',
            skill.candidateCount > 0 || skill.vacancyCount > 0
              ? 'cursor-not-allowed opacity-40'
              : 'cursor-pointer hover:bg-muted hover:text-destructive',
          )}
          onClick={() => setDeleteOpen(true)}
          disabled={skill.candidateCount > 0 || skill.vacancyCount > 0}
          aria-label="Delete skill"
          title={
            skill.candidateCount > 0 || skill.vacancyCount > 0
              ? 'Merge this skill into another one before deleting'
              : 'Delete skill'
          }
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Aliases
        </div>
        <div className="flex flex-wrap gap-1.5">
          {detail.aliases.map((alias) => {
            const isLast = detail.aliases.length <= 1;
            return (
              <span
                key={alias.id}
                className="inline-flex max-w-[200px] items-center gap-1.5 rounded-full border border-border bg-transparent px-2.5 py-1 text-xs text-muted-foreground"
              >
                <span className="truncate font-mono" title={alias.normalizedAlias}>
                  {alias.normalizedAlias}
                </span>
                {!isLast && (
                  <button
                    type="button"
                    onClick={() => removeAlias(alias.id)}
                    disabled={removeAliasMutation.isPending}
                    className="ml-0.5 cursor-pointer rounded-full p-0.5 transition-colors hover:bg-foreground/10 hover:text-foreground"
                    aria-label="Remove alias"
                    title="Remove alias"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
        <div className="mt-2 flex gap-2">
          <Input
            value={aliasDraft}
            maxLength={100}
            placeholder="Add alias (e.g. node.js)..."
            onChange={(e) => setAliasDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addAlias();
            }}
            className="h-8 flex-1 text-xs"
          />
          <Button
            size="sm"
            className="h-8 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
            onClick={addAlias}
            disabled={!aliasDraft.trim() || addAliasMutation.isPending}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="wrap-anywhere">
              Delete skill "{skill.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the skill and its {detail.aliases.length} alias(es). Only available
              when no candidate or vacancy references it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {mergeTarget && (
        <MergeConfirmDialog
          loser={skill}
          winner={mergeTarget}
          onClose={() => setMergeTarget(null)}
        />
      )}
    </div>
  );
}

// ── Merge picker (popover) ─────────────────────────────────────

function MergePicker({
  currentSkillId,
  onPick,
}: {
  currentSkillId: string;
  onPick: (skill: SkillAdminListItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Pull the full unfiltered list — independent from the outer search input.
  const { data: allSkills, isLoading } = useSkillsAdminList('');

  const filtered = useMemo(
    () =>
      (allSkills ?? [])
        .filter((s) => s.id !== currentSkillId)
        .filter((s) => s.name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 30),
    [allSkills, currentSkillId, query],
  );

  return (
    // `modal`: inside a modal Dialog the popover would be portaled outside the dialog's
    // scroll lock, so the wheel never reaches this list (same fix as HW-677).
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <GitMerge className="h-3 w-3" />
          Merge into…
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search winner skill..."
          className="mb-2 h-8 text-xs"
        />
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  onPick(s);
                }}
                className="flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted"
              >
                <span className="truncate text-foreground" title={s.name}>
                  {s.name}
                </span>
                <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">
                  {s.candidateCount}c · {s.vacancyCount}v
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Merge confirm dialog with preview ──────────────────────────

function MergeConfirmDialog({
  loser,
  winner,
  onClose,
}: {
  loser: SkillAdminListItem;
  winner: SkillAdminListItem;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<SkillMergePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mergeMutation = useMergeSkills();

  useEffect(() => {
    let cancelled = false;
    previewSkillMerge(loser.id, winner.id)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(extractConflictMessage(error) ?? 'Could not load preview');
      });
    return () => {
      cancelled = true;
    };
  }, [loser.id, winner.id]);

  const handleConfirm = () => {
    mergeMutation.mutate(
      { loserId: loser.id, winnerId: winner.id },
      {
        onSuccess: () => {
          toast.success(`"${loser.name}" merged into "${winner.name}"`);
          onClose();
        },
        onError: (error) => {
          toast.error(extractConflictMessage(error) ?? 'Could not merge skills');
        },
      },
    );
  };

  return (
    <AlertDialog open onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="wrap-anywhere">
            Merge "{loser.name}" into "{winner.name}"?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 wrap-anywhere text-sm">
              {loadError ? (
                <p className="text-destructive">{loadError}</p>
              ) : !preview ? (
                <p className="text-muted-foreground">Calculating impact…</p>
              ) : (
                <>
                  <p>
                    All data linked to <strong>{loser.name}</strong> will be repointed to{' '}
                    <strong>{winner.name}</strong>, then "{loser.name}" is deleted.
                  </p>
                  <ul className="ml-4 list-disc space-y-1 text-muted-foreground">
                    <li>{preview.aliasesToMove} alias(es) will move to the winner.</li>
                    <li>
                      {preview.candidatesAffected} candidate skill record(s) repointed
                      {preview.duplicateCandidateSkills > 0 && (
                        <> · {preview.duplicateCandidateSkills} duplicate(s) will collapse</>
                      )}
                      .
                    </li>
                    <li>
                      {preview.vacanciesAffected} vacancy skill record(s) repointed
                      {preview.duplicateVacancySkills > 0 && (
                        <> · {preview.duplicateVacancySkills} duplicate(s) will collapse</>
                      )}
                      .
                    </li>
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    Duplicate collapse keeps the strongest signal — MANUAL beats VACANCY,
                    verified wins over unverified, earliest createdAt is kept.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={!preview || mergeMutation.isPending}
            onClick={handleConfirm}
          >
            {mergeMutation.isPending ? 'Merging…' : 'Merge'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Error message helper ──────────────────────────────────────

function extractConflictMessage(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const payload = error.response?.data as
    | { message?: string | string[]; conflictingSkill?: SkillAliasConflict['conflictingSkill'] }
    | undefined;
  if (!payload) return null;
  if (Array.isArray(payload.message)) return payload.message[0] ?? null;
  return payload.message ?? null;
}
